import { useState, useCallback, useMemo } from 'react';

/**
 * useTableSelection - Reusable hook for table row selection and batch operations
 * 
 * Features:
 * - Individual row selection with checkboxes
 * - Select all / deselect all functionality
 * - Integration with filtered items (select only visible rows)
 * - Batch delete support
 * 
 * @param {Object} options
 * @param {Array} options.items - All available items
 * @param {Function} options.getItemId - Function to get item ID (default: item => item.id)
 * @returns {Object} Selection state and actions
 */
const useTableSelection = ({ items = [], getItemId = (item) => item.id } = {}) => {
  const [selectedRows, setSelectedRows] = useState(new Set());

  /**
   * Toggle single row selection
   */
  const toggleRow = useCallback((itemId) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  /**
   * Select a single row (add to selection)
   */
  const selectRow = useCallback((itemId) => {
    setSelectedRows(prev => new Set(prev).add(itemId));
  }, []);

  /**
   * Deselect a single row (remove from selection)
   */
  const deselectRow = useCallback((itemId) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      newSet.delete(itemId);
      return newSet;
    });
  }, []);

  /**
   * Select all rows from a given item list
   * @param {Array} itemsToSelect - Items to select (defaults to all items)
   */
  const selectAll = useCallback((itemsToSelect = items) => {
    const ids = itemsToSelect.map(getItemId);
    setSelectedRows(new Set(ids));
  }, [items, getItemId]);

  /**
   * Deselect all rows
   */
  const deselectAll = useCallback(() => {
    setSelectedRows(new Set());
  }, []);

  /**
   * Toggle select all (if all selected, deselect; otherwise select all)
   * @param {Array} itemsToToggle - Items to toggle (for filtered views)
   */
  const toggleSelectAll = useCallback((itemsToToggle = items) => {
    const ids = itemsToToggle.map(getItemId);
    const allSelected = ids.length > 0 && ids.every(id => selectedRows.has(id));
    
    if (allSelected) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(ids));
    }
  }, [items, getItemId, selectedRows]);

  /**
   * Check if a row is selected
   */
  const isSelected = useCallback((itemId) => {
    return selectedRows.has(itemId);
  }, [selectedRows]);

  /**
   * Check if all rows are selected
   * @param {Array} itemsToCheck - Items to check (for filtered views)
   */
  const isAllSelected = useCallback((itemsToCheck = items) => {
    if (itemsToCheck.length === 0) return false;
    return itemsToCheck.every(item => selectedRows.has(getItemId(item)));
  }, [items, getItemId, selectedRows]);

  /**
   * Check if some (but not all) rows are selected
   * @param {Array} itemsToCheck - Items to check (for filtered views)
   */
  const isSomeSelected = useCallback((itemsToCheck = items) => {
    if (itemsToCheck.length === 0) return false;
    const selectedCount = itemsToCheck.filter(item => selectedRows.has(getItemId(item))).length;
    return selectedCount > 0 && selectedCount < itemsToCheck.length;
  }, [items, getItemId, selectedRows]);

  /**
   * Get selected items (not just IDs)
   */
  const selectedItems = useMemo(() => {
    return items.filter(item => selectedRows.has(getItemId(item)));
  }, [items, selectedRows, getItemId]);

  /**
   * Get array of selected IDs
   */
  const selectedIds = useMemo(() => {
    return Array.from(selectedRows);
  }, [selectedRows]);

  /**
   * Clear selection (alias for deselectAll)
   */
  const clearSelection = deselectAll;

  /**
   * Remove items from selection (useful after delete)
   * @param {Array} idsToRemove - Array of IDs to remove from selection
   */
  const removeFromSelection = useCallback((idsToRemove) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      idsToRemove.forEach(id => newSet.delete(id));
      return newSet;
    });
  }, []);

  return {
    // State
    selectedRows,
    selectedCount: selectedRows.size,
    selectedIds,
    selectedItems,
    hasSelection: selectedRows.size > 0,

    // Actions
    toggleRow,
    selectRow,
    deselectRow,
    selectAll,
    deselectAll,
    toggleSelectAll,
    clearSelection,
    removeFromSelection,
    setSelectedRows,

    // Queries
    isSelected,
    isAllSelected,
    isSomeSelected,
  };
};

export default useTableSelection;
